import { Loader2 } from 'lucide-react'
import { ApiError } from '@/lib/api'
import type { PlatformAssociation } from './PlatformAssociationsListPage'

const easternDateTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'America/New_York',
})

function timestamp(value: string | null | undefined) {
  return value ? `${easternDateTime.format(new Date(value))} ET` : null
}

function isSeedingDemo(association: PlatformAssociation | undefined) {
  return association?.demo === true && association.seed_status === 'seeding'
}

function permanentReadFailure(error: Error | null) {
  return error instanceof ApiError && [401, 403, 404].includes(error.status)
}

export function demoSeedPollInterval(
  association: PlatformAssociation | undefined,
  error: Error | null,
) {
  if (!isSeedingDemo(association)) return false
  return permanentReadFailure(error) ? false : 3000
}

function completion(association: PlatformAssociation) {
  const rows = association.seeded_row_count
  return [
    ['Seeded', timestamp(association.seeded_at)].filter(Boolean).join(' '),
    rows == null ? null : `${rows.toLocaleString('en-GB')} rows`,
    'FAB profile',
    association.seeded_by ? `by ${association.seeded_by}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function PlatformDemoSeedStatus({ association }: { association: PlatformAssociation }) {
  if (association.seed_status === 'seeding') {
    return (
      <p className="mb-4 flex items-center gap-2 text-caption text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Seeding from the FAB profile…
      </p>
    )
  }
  if (association.seed_status === 'failed') {
    return (
      <p className="mb-4 text-caption text-destructive">
        {['Seed failed', timestamp(association.seed_failed_at), 'FAB profile']
          .filter(Boolean)
          .join(' · ')}
        <span className="ml-2 text-muted-foreground">
          Delete this association and create it again.
        </span>
      </p>
    )
  }
  if (association.seed_status === 'seeded') {
    return <p className="mb-4 text-caption text-muted-foreground">{completion(association)}</p>
  }
  return null
}
